import {
  memo, useState, useCallback, useMemo,
} from 'react';
import {
  PanelContent,
  Grid,
  CircularProgress,
  WidgetContainer,
  useTranslations,
  EditableContainer,
  useSnackbar,
  DetailsElements,
} from '@uniqkey-frontend/shared-app';
import { GetOrganizationByIdResponse } from '@uniqkey-backend-partner/api-client';
import { useUser } from '../../../../../../contexts/UserContext';
import { useUpdateOrganization } from '../../../../../../hooks/reactQuery';
import ACLEnum from '../../../../../../enums/ACLEnum';
import EditOrganizationDetailsModal, {
  IEditOrganizationDetailsModalReturnValue,
} from '../EditOrganizationDetailsModal';

interface IOrganizationDetailsWidgetProps {
  organizationId: string;
  organization: GetOrganizationByIdResponse;
  isLoading: boolean;
  isError: boolean;
}

const MIN_CONTAINER_HEIGHT = 305;

const OrganizationDetailsWidget = (props: IOrganizationDetailsWidgetProps) => {
  const {
    organizationId, organization, isLoading, isError,
  } = props;
  const { t } = useTranslations();
  const { showError, showSuccess } = useSnackbar();
  const { userCan } = useUser();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const handleEditModalOpen = useCallback(() => setIsEditModalOpen(true), []);
  const handleEditModalClose = useCallback(() => setIsEditModalOpen(false), []);

  const { partnerId } = organization ?? {} as GetOrganizationByIdResponse;

  const { mutate, isLoading: isPartnerUpdating } = useUpdateOrganization({
    organizationId,
    partnerId,
    useOptimisticUpdates: true,
  });

  const handleEditOrganizationDetails = useCallback((
    value: IEditOrganizationDetailsModalReturnValue,
  ) => {
    mutate(
      value,
      {
        onError: () => showError({ text: t('common.somethingWentWrong') }),
        onSuccess: () => {
          showSuccess({ text: t('organizationPage.toast.detailsUpdated') });
          handleEditModalClose();
        },
      },
    );
  }, [mutate, handleEditModalClose, showError, showSuccess, t]);

  const elements = useMemo(() => {
    const {
      name, address, vat, zip, billingEmail, country, phone, city, committedLicenseCount,
    } = organization ?? {};
    return {
      name, address, vat, zip, billingEmail, country, phone, city, committedLicenseCount,
    };
  }, [organization]);

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <PanelContent p={3} className="height-100-percent">
        <Grid container alignItems="center" justifyContent="center" className="height-100-percent">
          <CircularProgress />
        </Grid>
      </PanelContent>
    );
  }

  return (
    <WidgetContainer
      container
      p={0}
      pb={3}
      isLoading={isLoading}
      minHeight={MIN_CONTAINER_HEIGHT}
      withShadow
    >
      <EditableContainer
        container
        item
        p={3}
        rowGap={2}
        minHeight={MIN_CONTAINER_HEIGHT}
        onClick={handleEditModalOpen}
        disabled={!userCan(ACLEnum.OrganizationEdit)}
      >
        <DetailsElements
          hidden={!organization}
          elements={elements}
          translationKey="organizationDetailsWidget"
          t={t}
        />
      </EditableContainer>
      {isEditModalOpen && (
      <EditOrganizationDetailsModal
        isOpen={isEditModalOpen}
        isLoading={isPartnerUpdating}
        onSubmit={handleEditOrganizationDetails}
        onClose={handleEditModalClose}
        organization={organization!}
      />
      )}
    </WidgetContainer>
  );
};

export default memo(OrganizationDetailsWidget);
